'use client';

import {
  ActionSheet,
  type ActionSheetGroup
} from '@components/mobile/overlays/action-sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { useIsMobile } from '@hooks/use-mobile';
import { cn } from '@utils/cn';
import {
  cloneElement,
  Fragment,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useState
} from 'react';

export type ResponsiveAction = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Secondary line (stacked layout); for disabled actions, the reason. */
  description?: string;
  disabled?: boolean;
  destructive?: boolean;
  /** Desktop renders a link (inline layout); phones still call `onSelect`. */
  href?: string;
  /** File name for a download link. */
  download?: string;
  onSelect: () => void;
};

type ResponsiveActionMenuProps = {
  /** A button with an accessible name; opened by tap/click. */
  trigger: ReactElement<{ onClick?: (event: MouseEvent) => void }>;
  /** Sheet title on phones. */
  title?: ReactNode;
  /** Groups are separated; put destructive actions in their own group. */
  groups: ResponsiveAction[][];
  align?: 'start' | 'end';
  /**
   * `inline`: icon and label on one line. `stacked`: label over an optional
   * description, on the editor's frosted menu, with selection deferred until
   * the menu has closed so an action can open its own dialog.
   */
  layout?: 'inline' | 'stacked';
  /** Extra classes for the desktop menu surface. */
  contentClassName?: string;
  /** Extra classes for each desktop menu item. */
  itemClassName?: string;
  /** Controlled open state, e.g. to open the menu from a long-press. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const LAYOUT = {
  inline: {
    content: undefined,
    item: 'min-h-9 cursor-pointer gap-3 rounded-md px-3'
  },
  stacked: {
    content:
      'min-w-56 max-w-80 rounded-xl border-border/80 bg-popover/95 p-1 shadow-none backdrop-blur-2xl',
    item: 'min-h-11 cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 text-body-dense'
  }
} as const;

/**
 * One list of overflow actions: ActionSheet on phones, DropdownMenu on
 * desktop. Disabled actions stay visible (with their reason when given).
 */
export function ResponsiveActionMenu({
  trigger,
  title,
  groups,
  align = 'end',
  layout = 'inline',
  contentClassName,
  itemClassName,
  open: openProp,
  onOpenChange
}: ResponsiveActionMenuProps) {
  const isMobile = useIsMobile();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const visibleGroups = groups.filter(group => group.length > 0);
  const stacked = layout === 'stacked';

  if (isMobile) {
    const sheetGroups: ActionSheetGroup[] = visibleGroups.map(
      (group, index) => ({
        id: `group-${index}`,
        actions: group.map(action => ({
          id: action.id,
          label: action.label,
          icon: action.icon ? (
            <span
              className="[&>svg]:size-4"
              aria-hidden={stacked ? true : undefined}
            >
              {action.icon}
            </span>
          ) : undefined,
          description: action.description,
          disabled: action.disabled,
          destructive: action.destructive,
          onSelect: action.onSelect
        }))
      })
    );
    return (
      <>
        {isValidElement(trigger)
          ? cloneElement(trigger, {
              onClick: (event: MouseEvent) => {
                trigger.props.onClick?.(event);
                setOpen(true);
              }
            })
          : trigger}
        <ActionSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          groups={sheetGroups}
        />
      </>
    );
  }

  const item = cn(LAYOUT[layout].item, itemClassName);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={6}
        collisionPadding={12}
        className={cn(LAYOUT[layout].content, contentClassName) || undefined}
      >
        {visibleGroups.map((group, index) => (
          <Fragment key={`group-${index}`}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            {group.map(action =>
              action.href && !stacked ? (
                <DropdownMenuItem
                  key={action.id}
                  asChild
                  disabled={action.disabled}
                  className={cn(
                    item,
                    action.destructive &&
                      'text-destructive focus:text-destructive'
                  )}
                >
                  {/* A disabled link has no href, so it can't be followed. */}
                  <a
                    href={action.disabled ? undefined : action.href}
                    download={action.disabled ? undefined : action.download}
                    rel="noreferrer"
                    aria-disabled={action.disabled ? true : undefined}
                    tabIndex={action.disabled ? -1 : undefined}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  key={action.id}
                  disabled={action.disabled}
                  className={cn(
                    item,
                    action.destructive &&
                      'text-destructive focus:text-destructive'
                  )}
                  onSelect={
                    stacked
                      ? // Let the menu finish closing (and return focus)
                        // before an action opens a dialog of its own.
                        () => window.setTimeout(action.onSelect, 0)
                      : action.onSelect
                  }
                >
                  {stacked ? (
                    <>
                      {action.icon ? (
                        <span className="mt-0.5 [&>svg]:size-4" aria-hidden>
                          {action.icon}
                        </span>
                      ) : null}
                      <span className="flex min-w-0 flex-col">
                        <span>{action.label}</span>
                        {action.description ? (
                          <span className="text-caption text-muted-foreground">
                            {action.description}
                          </span>
                        ) : null}
                      </span>
                    </>
                  ) : (
                    <>
                      {action.icon}
                      <span>{action.label}</span>
                    </>
                  )}
                </DropdownMenuItem>
              )
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
